import { Fragment, useEffect, useMemo, useState } from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { PinIcon, SearchIcon, XIcon } from 'lucide-react';

import Button from 'components/app-components/ui/button';
import { CheckboxPrimary } from 'components/app-components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/app-components/ui/dialog';
import theraToast from 'components/generic-components/toast/TheraToast';
import type { ShortcutGroup, ShortcutOption } from './shortcutOptions';

interface ShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
  groups: ShortcutGroup[];
  selected: ShortcutOption[];
  maxSelectable: number;
  isSaving: boolean;
  onSave: (paths: string[]) => Promise<void>;
}

const ShortcutLabel = ({ label }: { label: string }) => {
  const parts = label.split(' › ');
  return (
    <span className="truncate">
      {parts.map((part, index) => (
        <Fragment key={part}>
          {index > 0 && <span className="mx-1 text-neutral-400">›</span>}
          <span className={index < parts.length - 1 ? 'text-neutral-500' : ''}>{part}</span>
        </Fragment>
      ))}
    </span>
  );
};

export const ShortcutsModal = ({
  isOpen,
  onClose,
  groups,
  selected,
  maxSelectable,
  isSaving,
  onSave,
}: ShortcutsModalProps) => {
  const [selectedPaths, setSelectedPaths] = useState<string[]>([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (isOpen) {
      setSelectedPaths(selected.map((option) => option.href));
      setQuery('');
    }
  }, [isOpen, selected]);

  const labelsByHref = useMemo(
    () => new Map(groups.flatMap((group) => group.options.map((option) => [option.href, option.label]))),
    [groups],
  );

  const isAtLimit = selectedPaths.length >= maxSelectable;

  const toggle = (href: string) => {
    setSelectedPaths((current) => {
      if (current.includes(href)) return current.filter((item) => item !== href);
      if (current.length >= maxSelectable) return current;
      return [...current, href];
    });
  };

  const handleSave = async () => {
    try {
      await onSave(selectedPaths);
      onClose();
    } catch {
      theraToast.error({ message: 'Could not save your shortcuts. Please try again.' });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isSaving && onClose()}>
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="px-6 pb-4 pt-6">
          <DialogTitle className="text-xl font-semibold text-neutral-900">Customize shortcuts</DialogTitle>
          <DialogDescription className="text-sm text-neutral-500">
            Pin the pages you use most. They show up at the bottom of your sidebar.
          </DialogDescription>
        </DialogHeader>

        <CommandPrimitive loop className="flex min-h-0 flex-1 flex-col">
          <div className="px-6">
            <div className="flex items-center gap-2 rounded-lg border border-neutral-300 px-3 transition-colors focus-within:border-therablue focus-within:ring-1 focus-within:ring-therablue">
              <SearchIcon className="size-4 shrink-0 text-neutral-400" />
              <CommandPrimitive.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder="Search pages"
                className="h-10 flex-1 border-0 bg-transparent p-0 text-sm text-neutral-900 shadow-none outline-none ring-0 placeholder:text-neutral-400 focus:border-0 focus:shadow-none focus:outline-none focus:ring-0"
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                  className="rounded p-0.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
                >
                  <XIcon className="size-4" />
                </button>
              )}
            </div>
          </div>

          <div className="px-6 pt-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium uppercase tracking-wide text-neutral-500">
                Pinned · {selectedPaths.length}/{maxSelectable}
              </span>
              {selectedPaths.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedPaths([])}
                  className="text-xs font-medium text-therablue hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
            <div className="mt-2 flex min-h-8 flex-wrap gap-1.5">
              {selectedPaths.length === 0 ? (
                <p className="text-sm text-neutral-400">Nothing pinned yet. Pick pages from the list below.</p>
              ) : (
                selectedPaths.map((href) => (
                  <span
                    key={href}
                    className="flex items-center gap-1.5 rounded-full bg-neutral-100 py-1 pl-2.5 pr-1 text-xs font-medium text-neutral-800"
                  >
                    <PinIcon className="size-3 text-neutral-500" />
                    {labelsByHref.get(href) ?? href}
                    <button
                      type="button"
                      aria-label={`Unpin ${labelsByHref.get(href) ?? href}`}
                      onClick={() => toggle(href)}
                      className="rounded-full p-0.5 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-900"
                    >
                      <XIcon className="size-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          <CommandPrimitive.List className="mt-4 max-h-80 min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-neutral-200 px-3 py-2">
            <CommandPrimitive.Empty className="px-3 py-8 text-center text-sm text-neutral-500">
              No pages match &ldquo;{query}&rdquo;
            </CommandPrimitive.Empty>
            {groups.map((group) => (
              <CommandPrimitive.Group
                key={group.label}
                heading={group.label}
                className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-neutral-400"
              >
                {group.options.map((option) => {
                  const isChecked = selectedPaths.includes(option.href);
                  return (
                    <CommandPrimitive.Item
                      key={option.href}
                      value={option.href}
                      keywords={[option.label, group.label]}
                      disabled={!isChecked && isAtLimit}
                      onSelect={() => toggle(option.href)}
                      className="flex cursor-pointer select-none items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-neutral-800 data-[disabled=true]:cursor-not-allowed data-[selected=true]:bg-neutral-100 data-[disabled=true]:opacity-50"
                    >
                      <ShortcutLabel label={option.label} />
                      <CheckboxPrimary checked={isChecked} tabIndex={-1} aria-hidden className="pointer-events-none" />
                    </CommandPrimitive.Item>
                  );
                })}
              </CommandPrimitive.Group>
            ))}
          </CommandPrimitive.List>
        </CommandPrimitive>

        <DialogFooter className="border-t border-neutral-200 px-6 py-4">
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isSaving}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
